import {
  ArrayInput,
  AutocompleteInput,
  Create,
  DateField,
  FormToolbar as AdminFormToolbar,
  ReferenceInput,
  SaveButton,
  SimpleFormIterator,
  TextInput,
  DeleteButton,
} from "@/components/admin";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQueryClient } from "@tanstack/react-query";
import {
  EditBase,
  Form,
  type GetListResult,
  type Identifier,
  required,
  Translate,
  useCreate,
  useCreatePath,
  useCreateSuggestionContext,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetManyAggregate,
  useNavigate,
  useNotify,
  useRecordContext,
  useTranslate,
  useSimpleFormIteratorItem,
  useUpdate,
  WithRecord,
} from "ra-core";
import {
  ArrowRight,
  Check,
  ChevronLeft,
  ExternalLink,
  Loader2,
  NotebookPen,
  Pencil,
  RefreshCw,
  Sparkles,
  Trash2,
} from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DealInputs } from "../deals/DealInputs";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { defaultTaskCategories } from "../root/defaultConfiguration";
import { DealOption } from "../misc/DealOption";
import type {
  Deal,
  DealNote,
  Sale,
  Task,
  ZoomMeetingParticipant,
  ZoomMeetingSummary,
  ZoomMeetingSummaryView,
} from "../types";
import { Markdown } from "../misc/Markdown";
import { parseTasksFromSummary } from "./parseTasksFromSummary";
import { resolveAssignee } from "./resolveAssignee";
import { AssigneeResolutionPanel } from "./AssigneeResolutionPanel";
import { MeetingTitle } from "../zoom_meetings/MeetingTitle";
import { isExternalDraft, tasksMissingSales } from "./assigneeRows";
import {
  buildTaskRow,
  categoryForSale,
  defaultDueDate,
  resolveAssignees,
} from "../tasks/taskUtils";
import { PlaneIcon } from "../tasks/PlaneIcon";
import { usePushTaskToPlane } from "../tasks/usePushTaskToPlane";
import { useHasPlaneKey } from "../settings/usePlaneKey";
import { planeDraftState } from "./planeDraftState";
import { Badge } from "@/components/ui/badge";
import {
  meetingClockRef,
  playableRecordings,
  RecordingPlayer,
  useRecordings,
} from "./RecordingPlayer";
import { StatusField } from "./StatusField";
import {
  MeetingSaveProvider,
  useReportDirty,
  useSaveMeeting,
} from "./useSaveMeeting";
import { LeaveGuard } from "./LeaveGuard";
import isEqual from "lodash/isEqual";
import { TimestampBadge } from "./TimestampBadge";
import { formatTime } from "./formatTime";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { TranscriptPanel } from "./TranscriptPanel";
import {
  type MeetingClock,
  type MeetingSpan,
  coverageShortfall,
  meetingSpan,
  mergedCues,
  mergedVtt,
  toMeetingTime,
  toRecordingTime,
} from "./meetingTimeline";
import { useSeekWhenReady } from "./useSeekWhenReady";
import { useMeetingLinkTarget } from "../zoom_meetings/useMeetingLinkTarget";
import { useMeetingAssets } from "../zoom_meetings/useMeetingAssets";
import { MeetingAssetBar } from "../zoom_meetings/MeetingAssetBar";
import { AssetPlaceholder } from "../zoom_meetings/AssetPlaceholder";
import { mediaState } from "../zoom_meetings/assetMessages";
import type { PlayerControl } from "./RecordingPlayer";
import { useTimestampMatching } from "./useTranscriptTimestamps";
import { useWatch, useFormContext } from "react-hook-form";
import {
  summaryEntries,
  summaryLink,
  type CustomSummary,
} from "./summaryEntries";
import { Textarea } from "@/components/ui/textarea";
import { usePasteAsMarkdown } from "../misc/usePasteAsMarkdown";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const DealCreate = () => {
  const { identity } = useGetIdentity();
  const { onCancel, onCreate, filter } = useCreateSuggestionContext();

  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();

  const onSuccess = async (deal: Deal) => {
    // increase the index of all deals in the same stage as the new deal
    // first, get the list of deals in the same stage
    const { data: allDeals } = await dataProvider.getList<Deal>("deals", {
      pagination: { page: 1, perPage: 1000 },
      filter: { stage: deal.stage },
    });
    const deals = allDeals.filter((d: Deal) => d.id !== deal.id);
    // update the actual deals in the database
    await Promise.all(
      deals.map(async (oldDeal) =>
        dataProvider.update("deals", {
          id: oldDeal.id,
          data: { index: oldDeal.index + 1 },
          previousData: oldDeal,
        }),
      ),
    );
    // refresh the list of deals in the cache as we used dataProvider.update(),
    // which does not update the cache
    const dealsById = deals.reduce(
      (acc, d) => ({
        ...acc,
        [d.id]: { ...d, index: d.index + 1 },
      }),
      {} as { [key: string]: Deal },
    );
    const now = Date.now();
    queryClient.setQueriesData<GetListResult | undefined>(
      { queryKey: ["deals", "getList"] },
      (res) => {
        if (!res) return res;
        return {
          ...res,
          data: res.data.map((d: Deal) => dealsById[d.id] || d),
        };
      },
      { updatedAt: now },
    );
    onCreate(deal);
  };

  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="sm:max-w-5xl overflow-y-auto max-h-9/10 top-1/20 translate-y-0">
        <Create
          redirect={false}
          resource="deals"
          mutationOptions={{
            onSuccess,
          }}
        >
          <Form
            className="flex flex-col gap-4"
            defaultValues={{
              sales_id: identity?.id,
              contact_ids: [],
              index: 0,
              name: filter,
            }}
          >
            <DealInputs />
            <AdminFormToolbar>
              <SaveButton />
            </AdminFormToolbar>
          </Form>
        </Create>
      </DialogContent>
    </Dialog>
  );
};

const salesFilterToQuery = (searchText: string) => ({
  "first_name@ilike": `%${searchText}%`,
});
const SalesSelector = () => {
  return (
    <ReferenceInput source="sales_id" reference="sales">
      <AutocompleteInput
        label="Assignee *"
        optionText={(record: { first_name: string; last_name: string }) =>
          `${record.first_name} ${record.last_name}`
        }
        className="w-full min-w-0"
        filterToQuery={salesFilterToQuery}
      />
    </ReferenceInput>
  );
};

// The contact this draft is about, carried onto the created task. Optional and
// clearable, like every other task form since LCATOMICZO-31. `parse` is required,
// not cosmetic: the clear control and re-picking the current choice both write
// "", which would sit in the JSONB draft and then be rejected by PostgREST as a
// bigint when the task is created.
const DraftContactSelector = () => {
  return (
    <ReferenceInput source="contact_id" reference="contacts" perPage={10}>
      <AutocompleteInput
        label="resources.tasks.fields.contact_id"
        parse={(value) => value || null}
        className="w-full min-w-0"
        helperText={false}
      />
    </ReferenceInput>
  );
};

// A saved summary is either the text itself or a link to a document elsewhere,
// told apart by summaryLink. LCCRM-88.
const SUMMARY_BODY_PLACEHOLDER = "Paste a document link or type the summary";

const linkHost = (link: string) => {
  try {
    return new URL(link).hostname.replace(/^www\./, "");
  } catch {
    return link;
  }
};

const SummaryPanel = () => {
  const [editing, setEditing] = useState(false);
  // A summary search excerpt links here with the summary it matched
  // (LCATOMICZO-76 phase 5). An unknown key falls back to the first entry below.
  const linkedSummary = useMeetingLinkTarget().summary;
  const [activeKey, setActiveKey] = useState<string | null>(
    linkedSummary ?? null,
  );
  const [adding, setAdding] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftBody, setDraftBody] = useState("");
  // The text as it was when edit mode opened. Cancel and Discard restore it —
  // the form holds the edited value, so it cannot be the source of the original.
  const [snapshot, setSnapshot] = useState<string | null>(null);
  // The summary the user is trying to switch to while an edit is unsaved. Set
  // means the Save/Discard prompt is open.
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const record = useRecordContext<ZoomMeetingSummary>();
  const { setValue, trigger, getValues } = useFormContext();
  const { identity } = useGetIdentity();
  const { save, canSave } = useSaveMeeting();
  const notify = useNotify();
  const summaryContent = useWatch({ name: "summary_content" });
  const pasteAsMarkdown = usePasteAsMarkdown();

  // Two sources on purpose. Zoom's narrative fields come from the record because
  // they are display-only: registering summary_details as an input would put it
  // in the values the form submits, where ra-data-postgrest diffs it against
  // previousData and a reserialized copy would be written back. The user's own
  // summaries come from the form because they are edited, and one just added has
  // to appear in the picker before the page is saved.
  const customSummaries = useWatch({ name: "custom_summaries" }) as
    | CustomSummary[]
    | undefined;

  const entries = summaryEntries(record, summaryContent, customSummaries);
  const active = entries.find((e) => e.key === activeKey) ?? entries[0];

  const isCustom =
    !!active && !!customSummaries?.some((c) => c.id === active.key);
  // Zoom's recap and topic sections are read-only. The next steps are the field
  // the form saves and parseTasksFromSummary reads; the user's own summaries are
  // theirs to change.
  const editable = active?.key === "next" || isCustom;

  // Only while edit mode is open, and only against the text it opened with. A
  // neutral click is not a decision, so nothing prompts on blur.
  const isDirty =
    editing && snapshot != null && (active?.body ?? "") !== snapshot;

  // Typing goes to the form; Save is what reaches the database. The column is
  // `not null`, so these always write an array, never null.
  const writeCustom = (next: CustomSummary[]) =>
    setValue("custom_summaries", next, { shouldDirty: true });

  const enterEdit = () => {
    setSnapshot(active?.body ?? "");
    setEditing(true);
  };

  const leaveEdit = () => {
    setEditing(false);
    setSnapshot(null);
  };

  const restoreSnapshot = () => {
    if (snapshot == null || !active) return;
    if (active.key === "next") {
      setValue("summary_content", snapshot, { shouldDirty: true });
      return;
    }
    writeCustom(
      (customSummaries ?? []).map((c) =>
        c.id === active.key ? { ...c, body: snapshot } : c,
      ),
    );
  };

  /** True when the edit was persisted. False leaves edit mode open on purpose. */
  const saveEdit = async (): Promise<boolean> => {
    if (!active) return false;
    if (!canSave) {
      notify("Still loading this meeting. Try again in a moment.", {
        type: "warning",
      });
      return false;
    }
    // Next steps is `required()`. Previewing text that was refused would read as
    // saved, so validation failure keeps the editor open with the error showing.
    if (active.key === "next" && !(await trigger("summary_content"))) {
      return false;
    }
    setSaving(true);
    try {
      // Read at send time, not from the render that handled the click: there is
      // an `await` above, and a keystroke landing in that window would otherwise
      // save the older text while the form holds the newer.
      const values = getValues();
      await save(
        active.key === "next"
          ? { summary_content: values.summary_content }
          : { custom_summaries: values.custom_summaries ?? [] },
      );
      leaveEdit();
      return true;
    } catch {
      notify("Couldn't save the summary. Your text is still here.", {
        type: "error",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    restoreSnapshot();
    leaveEdit();
  };

  const applyViewChange = (key: string) => {
    setActiveKey(key);
    leaveEdit();
  };

  const handleViewChange = (key: string) => {
    // Leaving an unsaved edit behind is the one thing this panel asks about.
    if (isDirty) {
      setPendingKey(key);
      return;
    }
    applyViewChange(key);
  };

  const handleAdd = async () => {
    if (!canSave) {
      notify("Still loading this meeting. Try again in a moment.", {
        type: "warning",
      });
      return;
    }
    const entry: CustomSummary = {
      id: crypto.randomUUID(),
      label: draftLabel.trim(),
      body: draftBody,
      created_at: new Date().toISOString(),
      sales_id: identity?.id ?? null,
    };
    const next = [...(customSummaries ?? []), entry];
    writeCustom(next);
    setSaving(true);
    try {
      // The dialog's button says Add, so it adds. Staging here is what lost every
      // summary anyone wrote in production.
      await save({ custom_summaries: next });
    } catch {
      notify("Couldn't add the summary.", { type: "error" });
      setSaving(false);
      return;
    }
    setSaving(false);
    setAdding(false);
    setDraftLabel("");
    setDraftBody("");
    setActiveKey(entry.id);
    leaveEdit();
  };

  const handleRemove = async () => {
    if (!active) return;
    if (!canSave) {
      notify("Still loading this meeting. Try again in a moment.", {
        type: "warning",
      });
      return;
    }
    // The only action here that retyping cannot undo.
    if (!window.confirm(`Remove the "${active.label}" summary?`)) return;
    const next = (customSummaries ?? []).filter((c) => c.id !== active.key);
    writeCustom(next);
    setSaving(true);
    try {
      await save({ custom_summaries: next });
    } catch {
      notify("Couldn't remove the summary.", { type: "error" });
      // `customSummaries` is the pre-removal array — this closure was made in the
      // render that handled the click, before writeCustom(next) ran. Reading
      // getValues() here would return `next` and remove it a second time.
      writeCustom(customSummaries ?? []);
      setSaving(false);
      return;
    }
    setSaving(false);
    setActiveKey(null);
    leaveEdit();
  };

  const handleCustomBody = (body: string) =>
    writeCustom(
      (customSummaries ?? []).map((c) =>
        c.id === active?.key ? { ...c, body } : c,
      ),
    );

  // So the leave prompt can name the summary and save it, not just warn.
  useReportDirty("summary", "the summary", isDirty, saveEdit);

  return (
    <div className="border rounded-lg overflow-hidden flex flex-col h-full">
      <div className="flex flex-wrap items-center gap-2 p-2 bg-muted/50 border-b shrink-0">
        <span className="text-sm font-medium text-muted-foreground">
          Summary
        </span>
        {/* Buttons, not a dropdown: a dropdown hid that Recap and By topic
            existed at all ("you don't know that really there's other things",
            Bill, meeting 476). They wrap rather than scroll. LCCRM-88. */}
        {entries.length > 1 && active && (
          <div className="flex flex-wrap gap-1">
            {entries.map((e) => {
              const className = cn(
                "inline-flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors",
                e.key === active.key
                  ? "bg-foreground text-background border-foreground"
                  : "bg-background hover:bg-muted",
              );
              // A summary that is only a link opens its document in a new tab:
              // the button is how Bill gets to the PDF he saved, "you just click
              // it, and it'll open" (LCCRM-88). A real link with target=_blank,
              // not window.open, so it is always a tab and never a blocked or
              // separate popup window. The click still selects the entry, so
              // Edit and Remove apply to it; an unsaved edit elsewhere is kept
              // behind the usual prompt.
              return e.link ? (
                <a
                  key={e.key}
                  href={e.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-current={e.key === active.key ? "true" : undefined}
                  onClick={() => handleViewChange(e.key)}
                  title={`Opens ${linkHost(e.link)} in a new tab`}
                  className={className}
                >
                  {e.label}
                  <ExternalLink className="size-3" />
                </a>
              ) : (
                <button
                  key={e.key}
                  type="button"
                  aria-pressed={e.key === active.key}
                  onClick={() => handleViewChange(e.key)}
                  className={className}
                >
                  {e.label}
                </button>
              );
            })}
          </div>
        )}
        <span className="flex-1" />
        {isCustom && (
          <button
            type="button"
            onClick={handleRemove}
            className="text-xs px-2 py-1 rounded border bg-background hover:bg-muted transition-colors"
          >
            Remove
          </button>
        )}
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="text-xs px-2 py-1 rounded border bg-background hover:bg-muted transition-colors"
        >
          Add summary
        </button>
        {editable &&
          (editing ? (
            <>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                className="text-xs px-2 py-1 rounded border bg-background hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={saving}
                className="text-xs px-2 py-1 rounded border bg-foreground text-background hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={enterEdit}
              className="text-xs px-2 py-1 rounded border bg-background hover:bg-muted transition-colors"
            >
              Edit
            </button>
          ))}
      </div>
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4">
          {editing && isCustom && active ? (
            // A plain textarea, not a TextInput: a custom summary lives at an
            // index inside a jsonb array, not at a form source of its own.
            <Textarea
              value={active.body}
              onChange={(e) => handleCustomBody(e.target.value)}
              onPaste={pasteAsMarkdown}
              className="min-h-64"
              placeholder={SUMMARY_BODY_PLACEHOLDER}
            />
          ) : editing && editable ? (
            <TextInput
              source="summary_content"
              multiline
              validate={required()}
              label={false}
              helperText={false}
              onPaste={pasteAsMarkdown}
            />
          ) : active?.link ? (
            <div className="flex flex-col items-start gap-2 text-sm">
              <span className="text-muted-foreground">Linked document</span>
              <span className="font-medium break-all">
                {linkHost(active.link)}
              </span>
              <Button asChild size="sm" variant="outline">
                <a href={active.link} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="size-3.5" />
                  Open
                </a>
              </Button>
            </div>
          ) : active ? (
            <Markdown>{active.body}</Markdown>
          ) : (
            <span className="text-muted-foreground text-sm">No summary</span>
          )}
        </div>
      </ScrollArea>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add a summary</DialogTitle>
            <DialogDescription>
              Anything you wrote or had written elsewhere.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Input
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              placeholder="Name, for example ChatGPT"
            />
            <Textarea
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              onPaste={pasteAsMarkdown}
              className="min-h-48"
              placeholder={SUMMARY_BODY_PLACEHOLDER}
            />
            {summaryLink(draftBody) && (
              <p className="text-xs text-muted-foreground">
                Link: opens {linkHost(summaryLink(draftBody)!)} in a new tab.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={handleAdd}
              disabled={!draftLabel.trim() || saving}
            >
              {saving ? "Adding..." : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Switching summaries would leave an unsaved edit behind, so it asks. */}
      <Dialog
        open={pendingKey != null}
        onOpenChange={(open) => {
          if (!open) setPendingKey(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save your changes?</DialogTitle>
            <DialogDescription>
              You have unsaved changes to this summary.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPendingKey(null)}
            >
              Keep editing
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const key = pendingKey;
                cancelEdit();
                setPendingKey(null);
                if (key != null) applyViewChange(key);
              }}
            >
              Discard
            </Button>
            <Button
              type="button"
              disabled={saving}
              onClick={async () => {
                const key = pendingKey;
                // A failed save keeps the prompt open — moving on would lose the
                // text the save just failed to store.
                if (!(await saveEdit())) return;
                setPendingKey(null);
                if (key != null) applyViewChange(key);
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ReParseButton = () => {
  const { getValues, setValue } = useFormContext();
  const { identity } = useGetIdentity();
  const record = useRecordContext<ZoomMeetingSummary>();
  // The meeting's attendee roster is the source of truth for assignee identity — each
  // row was already matched to a sales rep (by email) or a contact at ingestion, so we
  // resolve names against it instead of the whole sales/contacts tables.
  const { data: roster } = useGetList<ZoomMeetingParticipant>(
    "zoom_meeting_participants",
    {
      filter: { meeting_ref: record?.id },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: record?.id != null },
  );
  const { runMatching, timestampsLoading, timestampsProgress } =
    useContext(VideoContext);

  const handleReParse = async () => {
    const summaryContent = getValues("summary_content");
    if (!summaryContent) return;

    // Already-materialized drafts (task_id set) are preserved; only the rest are
    // regenerated, so a re-parse never clones or drops a created task.
    const current = (getValues("tasks") ?? []) as MatchedTask[];
    const created = current.filter((t) => t.task_id != null);
    const confirmMsg =
      created.length > 0
        ? `Re-parse tasks? ${created.length} already-created task${
            created.length > 1 ? "s" : ""
          } will be kept; the rest are regenerated.`
        : "This will replace all current tasks. Continue?";
    if (!window.confirm(confirmMsg)) return;

    const parsed = parseTasksFromSummary(summaryContent);

    const regenerated = parsed.flatMap((t) => {
      // A parsed item may cover several people ("Collaboration", "A/B", …) →
      // one member task per person, linked by a shared collaboration_id.
      const names = resolveAssignees(t.assignee_name, t.text);
      const collaborationId = names.length > 1 ? crypto.randomUUID() : null;
      const dueDate = defaultDueDate();
      return names.map((name) => {
        const binding = resolveAssignee(name, roster ?? []);
        if (binding.kind === "sales") {
          return {
            assignee_name: name,
            text: t.text,
            contact_id: undefined,
            sales_id: binding.sales_id,
            is_external: false,
            due_date: dueDate,
            collaboration_id: collaborationId,
          };
        }
        if (binding.kind === "contact") {
          return {
            assignee_name: name,
            text: t.text,
            contact_id: binding.contact_id,
            // sales_id on an external item is the internal follow-up owner (the rep
            // reviewing), not the assignee — the contact is the assignee.
            sales_id: identity?.id ?? undefined,
            is_external: true,
            due_date: dueDate,
            collaboration_id: collaborationId,
          };
        }
        if (binding.kind === "external") {
          // On the roster, not a teammate, and no contact record. Theirs to do, so it
          // stays a note on the meeting and never becomes a task. Re-parse is also how
          // a meeting stuck before this rule shipped gets unstuck: its drafts were
          // stored as unresolved and nothing re-derives them in place.
          return {
            assignee_name: name,
            text: t.text,
            contact_id: undefined,
            sales_id: identity?.id ?? undefined,
            is_external: true,
            due_date: dueDate,
            collaboration_id: collaborationId,
          };
        }
        // Unresolved: the name matched nobody on the roster, or matched two different
        // people — leave identity unset so the review UI surfaces it as "needs
        // assignment" instead of guessing.
        return {
          assignee_name: name,
          text: t.text,
          contact_id: undefined,
          sales_id: undefined,
          is_external: false,
          due_date: dueDate,
          collaboration_id: collaborationId,
        };
      });
    });

    // Drop any regenerated row that duplicates a kept (created) one — same
    // assignee + text — then append the fresh set after the preserved ones.
    const isDuplicateOfCreated = (r: {
      assignee_name?: string;
      text?: string;
    }) =>
      created.some(
        (c) => c.assignee_name === r.assignee_name && c.text === r.text,
      );
    setValue(
      "tasks",
      [...created, ...regenerated.filter((r) => !isDuplicateOfCreated(r))],
      { shouldDirty: true },
    );
  };

  const handleComputeTimestamps = async () => {
    await runMatching();
  };

  return (
    <div className="flex gap-1">
      <button
        type="button"
        onClick={handleReParse}
        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border bg-background hover:bg-muted transition-colors"
      >
        <RefreshCw className="h-3 w-3" />
        <Translate i18nKey="resources.zoom_meeting_summaries.action.reparse_tasks" />
      </button>
      <button
        type="button"
        onClick={handleComputeTimestamps}
        disabled={timestampsLoading}
        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border bg-background hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {timestampsLoading
          ? timestampsProgress || "Matching..."
          : "Match Timestamps"}
      </button>
    </div>
  );
};

const VideoContext = createContext<{
  videoRef: React.RefObject<MeetingClock | null>;
  timestamps: Record<number, number>;
  runMatching: () => Promise<void>;
  timestampsLoading: boolean;
  timestampsProgress: string;
  /** Seconds from meeting start to recording start: stored offset to shown time. */
  leadIn: number;
  meetingId?: Identifier;
}>({
  videoRef: { current: null },
  timestamps: {},
  runMatching: async () => {},
  timestampsLoading: false,
  timestampsProgress: "",
  leadIn: 0,
});

type MatchedTask = {
  text?: string;
  assignee_name?: string;
  offset_seconds?: number | null;
  match_basis?: "auto" | "manual";
  match_quote?: string;
  task_id?: Identifier | null;
};

// Accepts "m:ss", "mm:ss", "h:mm:ss", or a plain number of seconds.
// Returns seconds, or null when the text isn't a valid time.
function parseTimeInput(value: string): number | null {
  const s = value.trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  const parts = s.split(":");
  if (parts.some((p) => !/^\d+$/.test(p.trim()))) return null;
  const nums = parts.map((p) => parseInt(p, 10));
  if (nums.length === 2) return nums[0] * 60 + nums[1];
  if (nums.length === 3) return nums[0] * 3600 + nums[1] * 60 + nums[2];
  return null;
}

// Manual timestamp control — set/adjust/clear a task's time by hand. This is the
// path for items the matcher couldn't anchor (or to correct one it did).
const SetTimestampPopover = ({
  index,
  currentOffset,
  children,
}: {
  index: number;
  currentOffset?: number | null;
  children: React.ReactNode;
}) => {
  const { videoRef, leadIn } = useContext(VideoContext);
  const { setValue } = useFormContext();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (open)
      setDraft(
        typeof currentOffset === "number"
          ? formatTime(toMeetingTime(currentOffset, leadIn))
          : "",
      );
  }, [open, currentOffset, leadIn]);

  const apply = () => {
    const secs = parseTimeInput(draft);
    if (secs == null) return;
    // The input and the player's clock are meeting time; the stored offset is
    // recording time.
    setValue(`tasks.${index}.offset_seconds`, toRecordingTime(secs, leadIn), {
      shouldDirty: true,
    });
    setValue(`tasks.${index}.match_basis`, "manual", { shouldDirty: true });
    setOpen(false);
  };
  const clear = () => {
    // A deliberate human "no timestamp" — mark it manual so a re-match won't
    // silently re-anchor it. It renders as "Not in transcript" (offset null).
    setValue(`tasks.${index}.offset_seconds`, null, { shouldDirty: true });
    setValue(`tasks.${index}.match_basis`, "manual", { shouldDirty: true });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="flex w-56 flex-col gap-2 p-3">
        <p className="text-xs font-medium">Set timestamp</p>
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="mm:ss"
          className="h-8"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              apply();
            }
          }}
        />
        <button
          type="button"
          onClick={() => {
            if (videoRef.current)
              setDraft(formatTime(videoRef.current.currentTime));
          }}
          className="text-left text-xs text-primary hover:underline"
        >
          Use current video position
        </button>
        <div className="flex justify-end gap-1">
          {typeof currentOffset === "number" && (
            <Button type="button" variant="ghost" size="sm" onClick={clear}>
              Clear
            </Button>
          )}
          <Button type="button" size="sm" onClick={apply}>
            Set
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const TaskTimestampBadge = () => {
  const { videoRef, timestamps, leadIn } = useContext(VideoContext);
  // Live row identity from the iterator — the record context snapshot goes
  // stale after removals, and text-matching misfires on duplicate drafts.
  const { index } = useSimpleFormIteratorItem();
  const allTasks = useWatch({ name: "tasks" }) as MatchedTask[] | undefined;

  const task = allTasks?.[index];
  if (!task) return null;

  const offset =
    typeof task.offset_seconds === "number"
      ? task.offset_seconds
      : (timestamps[index] ?? undefined);

  // No timestamp (inferred follow-up not found in the recording, or legacy
  // data) → a clickable chip that opens the manual-set popover.
  if (offset === undefined) {
    return (
      <SetTimestampPopover index={index} currentOffset={task.offset_seconds}>
        <button
          type="button"
          title="Set a timestamp"
          className="inline-flex shrink-0 items-center whitespace-nowrap rounded bg-muted/60 px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted"
        >
          Not in transcript
        </button>
      </SetTimestampPopover>
    );
  }

  // Anchored: the timestamp badge (click to seek) + a pencil to edit/clear it.
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <TimestampBadge
        offset={offset}
        videoRef={videoRef}
        leadIn={leadIn}
        quote={task.match_quote || undefined}
      />
      <SetTimestampPopover index={index} currentOffset={offset}>
        <button
          type="button"
          title="Edit timestamp"
          className="p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </SetTimestampPopover>
    </div>
  );
};

// One-line summary above the task list: how many action items were anchored to
// the conversation vs flagged as inferred follow-ups not found in the recording.
const TaskMatchSummary = () => {
  const allTasks = useWatch({ name: "tasks" }) as MatchedTask[] | undefined;
  if (!allTasks?.length) return null;
  const hasMatchData = allTasks.some(
    (t) => t.match_basis || typeof t.offset_seconds === "number",
  );
  if (!hasMatchData) return null;
  const matched = allTasks.filter(
    (t) => typeof t.offset_seconds === "number",
  ).length;
  // Processed but with no timestamp = "not in transcript".
  const notFound = allTasks.filter(
    (t) => t.match_basis != null && typeof t.offset_seconds !== "number",
  ).length;
  return (
    <p className="text-xs text-muted-foreground -mt-1">
      {matched} of {allTasks.length} matched to the conversation
      {notFound > 0 ? ` · ${notFound} not in transcript` : ""}
    </p>
  );
};

// Why tasks show "Not in transcript": the recording did not cover the whole
// meeting (late start, early stop, or a gap between parts). Explains rather
// than warns, so it matches TaskMatchSummary instead of looking like an error.
const RecordingCoverageNote = ({ span }: { span: MeetingSpan }) => {
  const allTasks = useWatch({ name: "tasks" }) as MatchedTask[] | undefined;
  const shortfall = coverageShortfall(span);
  if (shortfall == null) return null;
  const meetingMinutes = Math.round(span.duration / 60);
  const recordedMinutes = Math.round((span.duration - shortfall) / 60);
  const anyNotFound = (allTasks ?? []).some(
    (t) => t.match_basis != null && typeof t.offset_seconds !== "number",
  );
  return (
    <p className="text-xs text-muted-foreground -mt-1">
      Recording covers {recordedMinutes} of {meetingMinutes} minutes.
      {anyNotFound
        ? " Tasks from the unrecorded part show as Not in transcript."
        : ""}
    </p>
  );
};

// Per-row "promote this draft into a real Action Item": creates one `tasks` row
// (guarded — needs an assignee), stamps `task_id` back onto the draft, and persists
// it (flipping the meeting to `done` once every draft is created). Reuses the
// per-row index pattern from TaskTimestampBadge.
//
// Called once per row (DraftRowActions) and handed to both of its buttons,
// because either can start it: Confirm, and Push to Plane on a draft that is not
// confirmed yet. The in-flight promise is what keeps a click on each from
// creating the task twice.
const useConfirmDraft = () => {
  const { meetingId } = useContext(VideoContext);
  // Live row identity from the iterator (see TaskTimestampBadge).
  const { index } = useSimpleFormIteratorItem();
  const { setValue, getValues } = useFormContext();
  const [create] = useCreate();
  const { save: saveMeeting, canSave } = useSaveMeeting();
  const notify = useNotify();
  const [confirming, setConfirming] = useState(false);
  const inFlight = useRef<Promise<Identifier | null> | null>(null);
  // Teammate roster for category derivation (category = assignee's role).
  const { data: salesRoster } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  const run = async (quiet: boolean): Promise<Identifier | null> => {
    if (meetingId == null) return null;
    // Checked before the task is created, not after: the write that stamps
    // task_id back onto the draft has to be allowed to happen, or we would
    // leave a real task in the CRM that this meeting has no record of.
    if (!canSave) {
      notify("Still loading this meeting. Try again in a moment.", {
        type: "warning",
      });
      return null;
    }
    const draft = getValues(
      `tasks.${index}`,
    ) as ZoomMeetingSummary["tasks"][number];
    if (draft.task_id != null) return draft.task_id;
    if (draft.sales_id == null) {
      notify("Assign a teammate to this task before creating it.", {
        type: "warning",
      });
      return null;
    }
    setConfirming(true);
    try {
      const created = await create(
        "tasks",
        { data: buildTaskRow(draft, meetingId, salesRoster) },
        { returnPromise: true },
      );
      setValue(`tasks.${index}.task_id`, created.id, { shouldDirty: true });
      // Persist the tasks array (with the new task_id); mark the meeting done once
      // every actionable draft has been materialized. Customer-bound (hidden)
      // drafts are never created, so they don't count.
      const updated = getValues("tasks") as ZoomMeetingSummary["tasks"];
      const allCreated = updated.every(
        (t) => !t.text || t.task_id != null || isExternalDraft(t),
      );
      await saveMeeting({
        tasks: updated,
        ...(allCreated ? { status: "done" as const } : {}),
      });
      if (allCreated) setValue("status", "done", { shouldDirty: false });
      if (!quiet) notify("Task created.", { type: "success" });
      return created.id as Identifier;
    } catch {
      notify("Failed to create the task.", { type: "error" });
      return null;
    } finally {
      setConfirming(false);
    }
  };

  // Resolves to the task's id, or null when nothing was created (the reason has
  // been shown). `quiet` drops the success toast for a caller that follows up
  // with its own.
  const confirm = ({ quiet = false }: { quiet?: boolean } = {}) => {
    inFlight.current ??= run(quiet).finally(() => {
      inFlight.current = null;
    });
    return inFlight.current;
  };

  return { confirm, confirming, canSave, salesRoster };
};

type ConfirmDraft = ReturnType<typeof useConfirmDraft>;

// Confirm, which becomes a linked "Go to Task" once the task exists.
const CreateTaskButton = ({
  confirm,
  confirming,
  canSave,
}: Pick<ConfirmDraft, "confirm" | "confirming" | "canSave">) => {
  const { index } = useSimpleFormIteratorItem();
  const allTasks = useWatch({ name: "tasks" }) as
    | ZoomMeetingSummary["tasks"]
    | undefined;

  const task = allTasks?.[index];
  if (!task) return null;

  if (task.task_id != null) {
    return (
      <Button
        asChild
        size="sm"
        className="h-7 flex-1 gap-1 px-2 text-xs bg-emerald-600 text-white hover:bg-emerald-700"
      >
        <a href={`#/tasks?taskId=${task.task_id}`}>
          Go to Task
          <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => void confirm()}
      disabled={confirming || task.sales_id == null || !canSave}
      title={task.sales_id == null ? "Assign a teammate first" : undefined}
      className="h-7 flex-1 gap-1 px-2 text-xs cursor-pointer text-emerald-600 dark:text-emerald-400 border-emerald-600/40 hover:bg-emerald-600/10 hover:text-emerald-600"
    >
      Confirm
      {confirming ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Check className="h-3.5 w-3.5" />
      )}
    </Button>
  );
};

// The row's Plane line, under Confirm / Go to Task. Outlined "Push to Plane"
// until the task is in Plane, then a filled link to the Plane item — which is
// also how the meeting shows that a task was already pushed, from here or from
// Action Items. Dev rows only, like every other push. LCCRM-92.
const PlaneDraftButton = ({
  confirm,
  confirming,
  canSave,
  salesRoster,
}: ConfirmDraft) => {
  const { index } = useSimpleFormIteratorItem();
  const allTasks = useWatch({ name: "tasks" }) as
    | ZoomMeetingSummary["tasks"]
    | undefined;
  const translate = useTranslate();
  const notify = useNotify();
  const hasPlaneKey = useHasPlaneKey();
  const pushTask = usePushTaskToPlane();
  const [pushing, setPushing] = useState(false);

  const draft = allTasks?.[index];
  // The draft only carries task_id; whether that task is in Plane lives on the
  // task. useGetManyAggregate folds every row's lookup into one request.
  const taskId = draft?.task_id ?? null;
  const { data } = useGetManyAggregate<Task>(
    "tasks",
    { ids: taskId != null ? [taskId] : undefined },
    { enabled: taskId != null },
  );
  const task = data?.[0];

  if (!draft) return null;
  const state = planeDraftState({ draft, task, sales: salesRoster });

  if (state === "go") {
    return (
      <Button
        asChild
        size="sm"
        className="h-7 gap-1 px-2 text-xs bg-blue-600 text-white hover:bg-blue-700"
      >
        <a
          href={task?.plane_url ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          title={task?.plane_identifier ?? undefined}
        >
          {translate("crm.plane.go_to_plane")}
          <PlaneIcon className="h-3.5 w-3.5" />
        </a>
      </Button>
    );
  }
  // Mid-push the row has just been confirmed and its task may not have loaded
  // yet, which reads as hidden; keep the spinner on screen until it settles.
  if (state === "hidden" && !pushing) return null;

  const handlePush = async () => {
    setPushing(true);
    try {
      // Before anything is created: without a key the push is refused, and a
      // click on Push to Plane should not leave a confirmed task as its only
      // effect.
      if (!(await hasPlaneKey())) {
        notify("crm.plane.no_key", { type: "warning" });
        return;
      }
      const id = draft.task_id ?? (await confirm({ quiet: true }));
      // A push that fails after the confirm leaves the task confirmed and this
      // button still offering the push.
      if (id != null) await pushTask(id);
    } catch {
      notify("crm.plane.push_failed", { type: "error" });
    } finally {
      setPushing(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handlePush}
      disabled={pushing || confirming || !canSave}
      className="h-7 gap-1 px-2 text-xs cursor-pointer text-blue-600 dark:text-blue-400 border-blue-600/40 hover:bg-blue-600/10 hover:text-blue-600"
    >
      {translate("crm.plane.push_to_plane")}
      {pushing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
    </Button>
  );
};

// The right column's buttons. One useConfirmDraft for the whole row: see there.
const DraftRowActions = () => {
  const confirmDraft = useConfirmDraft();
  return (
    <>
      <div className="flex items-center gap-1">
        <RemoveDraftButton />
        <CreateTaskButton {...confirmDraft} />
      </div>
      <PlaneDraftButton {...confirmDraft} />
    </>
  );
};

// Visible per-row remove (the iterator's rail control is disabled). Hidden once
// the draft is materialized — removing it would orphan the Created marker.
const RemoveDraftButton = () => {
  const { index, remove } = useSimpleFormIteratorItem();
  const allTasks = useWatch({ name: "tasks" }) as
    | ZoomMeetingSummary["tasks"]
    | undefined;
  if (allTasks?.[index]?.task_id != null) return null;
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      onClick={() => remove()}
      title="Remove draft"
      className="size-7 cursor-pointer text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  );
};

// Read-only hint on the row: the category the created task will get, derived
// from the assignee's role. Watches live form state (the record context inside
// the iterator goes stale after Resolution Map rebinds).
const DerivedCategoryBadge = () => {
  const { index } = useSimpleFormIteratorItem();
  const allTasks = useWatch({ name: "tasks" }) as
    | ZoomMeetingSummary["tasks"]
    | undefined;
  const { taskCategories } = useConfigurationContext();
  const { data: salesRoster } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });
  const live = allTasks?.[index];
  if (live?.sales_id == null) return null;
  const token = categoryForSale(live.sales_id, salesRoster, "");
  const label = (taskCategories ?? defaultTaskCategories).find(
    (c) => c.value === token,
  )?.label;
  if (!label) return null;
  return (
    // Inverted against the page: this is the one thing on the row nobody picked,
    // so it has to read as a result rather than as another muted control.
    <Badge
      className="bg-foreground text-background text-[10px] shrink-0 shadow-sm"
      title="Derived from the assignee's role"
    >
      {label}
    </Badge>
  );
};

// Simplified creation row: text + timestamp, then teammate, the contact it is
// about, and the derived category. Due date is defaulted (+7d) and the type
// concept is retired. The contact is optional and carries onto the created task
// (LCATOMICZO-31); it does not decide who the task belongs to — the is_external
// flag does that, and flagged drafts render only a marker, since the list
// wrapper hides their <li> entirely. The Resolution Map is where those come back.
// One horizontal row per draft: [assignee, contact] | task text + role stamp |
// [timestamp, trash, create]. Side columns are fixed-narrow verticals, text
// takes the rest and fills the row's height.
const TaskInputs = () => {
  const { index } = useSimpleFormIteratorItem();
  const allTasks = useWatch({ name: "tasks" }) as
    | ZoomMeetingSummary["tasks"]
    | undefined;
  const live = allTasks?.[index];
  if (live && isExternalDraft(live)) return <div data-external-hidden />;

  return (
    <div className="flex items-stretch gap-2 w-full border-b pb-2 mb-1">
      {/* Left: who owns it, then who it is about. */}
      <div className="flex w-36 shrink-0 flex-col gap-1">
        <SalesSelector />
        <DraftContactSelector />
      </div>
      {/* Middle: the task itself, filling the row's height, with the derived
          category sitting in its bottom-right corner. The badge belongs to the
          task the row will become, not to any one picker, so it reads better
          stamped on the composer than stacked among the inputs.
          pointer-events-none keeps it from stealing clicks into the textarea. */}
      <div className="relative flex flex-1 min-w-0">
        <TextInput
          source="text"
          multiline
          validate={required()}
          className="flex-1 min-w-0 grid-rows-[auto_1fr]"
          inputClassName="h-full"
          helperText={false}
        />
        <div className="absolute bottom-2 right-2 pointer-events-none">
          <DerivedCategoryBadge />
        </div>
      </div>
      {/* Right: timestamp on top; below it one row — square delete on the
          left (harder to misclick next to the wide button), Create/Go-to-Task
          taking the rest; then, on Dev rows, the Plane button. */}
      <div className="flex w-32 shrink-0 flex-col items-stretch gap-1">
        <div className="flex justify-end">
          <TaskTimestampBadge />
        </div>
        <DraftRowActions />
      </div>
    </div>
  );
};

// The task drafts' own Save and Cancel. They used to be the page's, which
// overclaimed: this is the only thing they ever wrote.
//
// Dirtiness is a deep compare against the record, and it must ignore key order:
// Postgres stores jsonb keys canonically, not as written, so a draft comes back
// reordered and a stringify comparison would read as dirty forever.
const useTasksDirty = () => {
  const record = useRecordContext<ZoomMeetingSummary>();
  const tasks = useWatch({ name: "tasks" }) as
    | ZoomMeetingSummary["tasks"]
    | undefined;
  return !isEqual(tasks ?? [], record?.tasks ?? []);
};

const TaskListControls = () => {
  const record = useRecordContext<ZoomMeetingSummary>();
  const { setValue, getValues } = useFormContext();
  const { save, canSave } = useSaveMeeting();
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const dirty = useTasksDirty();

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!canSave) {
      notify("Still loading this meeting. Try again in a moment.", {
        type: "warning",
      });
      return false;
    }
    setSaving(true);
    try {
      // Read at send time, as phase 2's audit required.
      await save({ tasks: getValues("tasks") ?? [] });
      return true;
    } catch {
      notify("Couldn't save the tasks. Your changes are still here.", {
        type: "error",
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [canSave, getValues, notify, save]);

  const handleCancel = () => {
    // The record is the last saved state — every task write goes through the
    // queue, and useUpdate reconciles the record after each one. No snapshot.
    setValue("tasks", record?.tasks ?? [], { shouldDirty: false });
  };

  useReportDirty("tasks", "the tasks", dirty, handleSave);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        onClick={handleCancel}
        disabled={!dirty || saving}
      >
        Cancel
      </Button>
      <Button type="button" onClick={handleSave} disabled={!dirty || saving}>
        {saving ? "Saving..." : "Save tasks"}
      </Button>
    </>
  );
};

// "Create all remaining". Rebuilt off <SaveButton>: as a SaveButton it submitted
// every form value through saveContext.save, which meant it wrote
// custom_summaries (sweeping along an open summary edit) and ran as a second,
// unserialized writer that could be overwritten by — or overwrite — a queued
// write landing during its creates. It keeps form.handleSubmit, which is what
// ran whole-form validation, and writes { tasks, status } through the queue.
const ProcessButton = () => {
  const [create] = useCreate();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const translate = useTranslate();
  const { getValues, handleSubmit, setValue } = useFormContext();
  const { save, canSave } = useSaveMeeting();
  const [busy, setBusy] = useState(false);
  // Teammate roster for category derivation (category = assignee's role).
  const { data: salesRoster } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  const createRemaining = async () => {
    const meetingId = getValues("id") as Identifier;
    // The deal note is logged separately (LogToDealButton). This materializes
    // only the drafts that aren't already created (task_id unset), stamping the
    // new id back onto each so they flip to "Created".
    const tasks = [
      ...((getValues("tasks") ?? []) as ZoomMeetingSummary["tasks"]),
    ];
    // A teammate is required on every actionable draft. Only the not-yet-created
    // ones get materialized, so only they need one.
    const missing = tasksMissingSales(tasks.filter((t) => t.task_id == null));
    if (missing.length > 0) {
      notify(
        `Assign a sales rep to ${missing.length} task${
          missing.length > 1 ? "s" : ""
        } before creating them.`,
        { type: "warning" },
      );
      return;
    }
    if (!canSave) {
      notify("Still loading this meeting. Try again in a moment.", {
        type: "warning",
      });
      return;
    }
    setBusy(true);
    try {
      for (let i = 0; i < tasks.length; i++) {
        const draft = tasks[i];
        // Skip empties, already-created drafts, and anything owned by someone
        // outside the company — never materialized.
        if (!draft.text || draft.task_id != null || isExternalDraft(draft)) {
          continue;
        }
        const created = await create(
          "tasks",
          { data: buildTaskRow(draft, meetingId, salesRoster) },
          { returnPromise: true },
        );
        tasks[i] = { ...draft, task_id: created.id };
      }
      // Meeting is done once every actionable draft has been materialized
      // (drafts owned by an outsider don't count — they stay hidden).
      const allCreated = tasks.every(
        (t) => !t.text || t.task_id != null || isExternalDraft(t),
      );
      // Keep the form in step with the stamps just written, so the rows flip to
      // "Created" without leaving the page.
      setValue("tasks", tasks, { shouldDirty: false });
      await save({
        tasks,
        ...(allCreated ? { status: "done" as const } : {}),
      });
      queryClient.invalidateQueries({
        queryKey: ["zoom_meetings", "getList"],
      });
      notify("Tasks created.", { type: "success" });
    } catch {
      notify("Couldn't create the tasks.", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      // handleSubmit runs the resolver, which is the whole-form validation this
      // used to get from being a SaveButton. The invalid branch says so out
      // loud: the field errors render inline, but without this the button just
      // appears to do nothing.
      onClick={handleSubmit(createRemaining, () =>
        notify("Fix the highlighted fields first.", { type: "warning" }),
      )}
      disabled={busy}
      className="bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600 border-0"
    >
      <Sparkles className="h-4 w-4" />
      {translate("resources.zoom_meeting_summaries.action.process")}
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
    </Button>
  );
};

// Logs the meeting summary onto the linked deal as a one-time deal note.
// Deduped via the note's own `zoom_meeting_id` back-reference (anchored to the
// meeting — the permanent, first-class entity, not the optional summary): if a
// note already exists for this meeting, the button shows "Logged" + an "Update
// note" that refreshes it, never a duplicate.
const LogToDealButton = () => {
  const record = useRecordContext<ZoomMeetingSummary>();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [busy, setBusy] = useState(false);

  const dealId = useWatch({ name: "deal_id" }) as Identifier | null | undefined;
  const summaryContent = useWatch({ name: "summary_content" }) as
    | string
    | undefined;

  const meetingRef = record?.id;
  const meetingStart = record?.meeting_start_time;

  // The dedup marker is the note itself — a deal note carrying this meeting's id.
  const { data: existing } = useGetList<DealNote>(
    "deal_notes",
    {
      filter: { zoom_meeting_id: meetingRef },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: meetingRef != null },
  );
  const loggedNote = existing?.[0];

  if (!dealId) {
    return (
      <p className="text-xs text-muted-foreground">
        Link a deal above to log the summary to it.
      </p>
    );
  }

  const refreshLogged = () =>
    queryClient.invalidateQueries({ queryKey: ["deal_notes"] });

  const handleLog = async () => {
    if (meetingRef == null) return;
    setBusy(true);
    try {
      await create(
        "deal_notes",
        {
          data: {
            deal_id: dealId,
            text: summaryContent ?? "",
            date: meetingStart,
            sales_id: identity?.id,
            zoom_meeting_id: meetingRef,
          },
        },
        { returnPromise: true },
      );
      await refreshLogged();
      notify("Summary logged to the deal.", { type: "success" });
    } catch {
      notify("Failed to log the summary to the deal.", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async () => {
    if (loggedNote == null) return;
    setBusy(true);
    try {
      await update(
        "deal_notes",
        {
          id: loggedNote.id,
          data: { text: summaryContent ?? "", date: meetingStart },
          previousData: loggedNote,
        },
        { returnPromise: true },
      );
      await refreshLogged();
      notify("Deal note updated.", { type: "success" });
    } catch {
      notify("Failed to update the deal note.", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (loggedNote != null) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          <Check className="h-3.5 w-3.5" /> Logged to deal
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleUpdate}
          disabled={busy}
          className="h-6 px-2 text-xs cursor-pointer"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Update note"}
        </Button>
      </div>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleLog}
      disabled={busy}
      className="self-start gap-1 cursor-pointer"
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <NotebookPen className="h-4 w-4" />
      )}
      Log summary to deal
    </Button>
  );
};

// The Deal section (right column, between the transcript and the tasks): pick
// the linked deal + log the summary to it as a deal note.
// The deal belongs to neither the summary nor the task list, and it is a single
// discrete choice undone by picking again, so it saves on selection. A scalar
// compare against the record, so none of the jsonb key-order trouble applies.
const DealAutosave = () => {
  const record = useRecordContext<ZoomMeetingSummary>();
  const { save, canSave } = useSaveMeeting();
  const notify = useNotify();
  const dealId = useWatch({ name: "deal_id" }) as Identifier | null | undefined;
  const recordDealId = record?.deal_id ?? null;

  useEffect(() => {
    const picked = dealId ?? null;
    if (picked === recordDealId || !canSave) return;
    save({ deal_id: picked }).catch(() => {
      notify("Couldn't link the deal.", { type: "error" });
    });
  }, [dealId, recordDealId, canSave, save, notify]);

  return null;
};

const DealSection = () => {
  return (
    <div className="border rounded-lg h-full flex flex-col overflow-hidden">
      <div className="flex items-center gap-2 p-2 bg-muted/50 border-b shrink-0">
        <span className="text-sm font-medium text-muted-foreground">Deal</span>
      </div>
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-3 flex flex-col gap-2">
          <ReferenceInput source="deal_id" reference="deals">
            <AutocompleteInput
              label={false}
              helperText={false}
              optionText={<DealOption />}
              create={<DealCreate />}
              createItemLabel="crm.action.create_item"
            />
          </ReferenceInput>
          <LogToDealButton />
          <DealAutosave />
        </div>
      </ScrollArea>
    </div>
  );
};

const BackButton = () => {
  const navigate = useNavigate();
  const createPath = useCreatePath();
  const to = createPath({
    resource: "zoom_meetings",
    type: "list",
  });

  return (
    <Button
      type="button"
      variant="ghost"
      className="-ml-3"
      onClick={(e) => {
        e.preventDefault();
        navigate(to);
      }}
    >
      <ChevronLeft className="size-6 -ml-2" />
      <Translate i18nKey="crm.common.back_to_list" />
    </Button>
  );
};

export const EditContent = () => {
  const record = useRecordContext<ZoomMeetingSummary>();
  const { save: saveMeeting } = useSaveMeeting();
  const videoRef = useRef<MeetingClock>(null);
  const [videoSwitchKey, setVideoSwitchKey] = useState(0);
  // A meeting link (`?asset=&t=`) from the list icons or a transcript excerpt.
  const link = useMeetingLinkTarget();
  // Per-asset state from the same view row the list reads.
  const assets = useMeetingAssets(record?.id);
  const playerRef = useRef<PlayerControl | null>(null);

  // Prevent page-level scroll — this page is a fixed-viewport grid
  useEffect(() => {
    const html = document.documentElement;
    const main = document.getElementById("main-content");
    html.style.overflow = "hidden";
    if (main) {
      main.style.overflow = "hidden";
      main.style.height = "calc(100vh - 48px)";
    }
    return () => {
      html.style.overflow = "";
      if (main) {
        main.style.overflow = "";
        main.style.height = "";
      }
    };
  }, []);
  const { data: recordings } = useRecordings(
    record?.id as number | undefined,
    record ? meetingClockRef(record) : null,
  );
  const { setValue, getValues } = useFormContext();
  const tasks = useWatch({ name: "tasks" }) as
    | ZoomMeetingSummary["tasks"]
    | undefined;

  // Timestamps are stored in each task's offset_seconds field (persisted in DB)
  const timestamps: Record<number, number> = {};
  tasks?.forEach((t, i) => {
    const offset = (t as Record<string, unknown>).offset_seconds;
    if (typeof offset === "number") timestamps[i] = offset;
  });

  const {
    match,
    loading: timestampsLoading,
    progress: timestampsProgress,
  } = useTimestampMatching(record?.id as number | undefined);

  // The meeting from start to end with every recorded part in place. The panel
  // shows every part's transcript in meeting time; the matcher gets them merged
  // into one VTT in recording time, so the offsets it returns are stored as they
  // are. Before this it read only the first transcript and a later part's items
  // could never match.
  const span = useMemo(
    () => meetingSpan(record, recordings),
    [record, recordings],
  );
  const leadIn = span.leadIn;
  const transcriptCues = useMemo(
    () => mergedCues(span.parts, span.leadIn),
    [span],
  );
  const vttContent = useMemo(() => {
    const cues = mergedCues(span.parts);
    return cues.length > 0 ? mergedVtt(cues) : undefined;
  }, [span]);

  const runMatching = useCallback(async () => {
    const currentTasks = getValues("tasks") as
      | Array<Record<string, unknown>>
      | undefined;
    if (!vttContent || !currentTasks?.length) return;
    await match(currentTasks, vttContent, (updated) =>
      setValue("tasks", updated, { shouldDirty: true }),
    );
  }, [getValues, vttContent, match, setValue]);

  // Auto-run timestamp matching once when the transcript becomes available.
  // Bails if any task already has offset_seconds (assume matching already ran).
  const autoMatchedRef = useRef(false);
  useEffect(() => {
    if (autoMatchedRef.current) return;
    if (!vttContent || !tasks?.length) return;
    if (timestampsLoading) return;
    // "Matched" = any task carries matcher output. Check match_basis too, not
    // just offset_seconds — a meeting where every item is "not in transcript"
    // has no offsets but has still been processed, and must not re-run.
    const alreadyMatched = tasks.some((t) => {
      const rec = t as Record<string, unknown>;
      return rec.match_basis != null || typeof rec.offset_seconds === "number";
    });
    if (alreadyMatched) {
      autoMatchedRef.current = true;
      return;
    }
    autoMatchedRef.current = true;
    runMatching();
  }, [vttContent, tasks, timestampsLoading, runMatching]);

  // What is playable comes from the rows (it needs their links); what to say
  // when nothing is comes from the view's per-asset state, through the same
  // messages the asset bar and the list use.
  const playableRecs = playableRecordings(recordings);
  const hasPlayable = playableRecs.length > 0;

  // `t` is meeting time, like a cue's start, which is what the clock takes.
  // `t` is recording time, like a stored offset; the clock takes meeting time.
  const linkTime = link.t == null ? null : toMeetingTime(link.t, leadIn);
  useSeekWhenReady(videoRef, linkTime, hasPlayable);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center gap-4 shrink-0 py-2">
        <BackButton />
        <WithRecord<ZoomMeetingSummaryView>
          render={(r) => (
            <MeetingTitle
              record={r}
              // Through the page's save queue, so an open summary or task edit
              // is neither overwritten nor lost. LCCRM-88.
              onSave={(title) => saveMeeting({ custom_title: title })}
            />
          )}
        />
        <StatusField />
        <div className="text-sm text-muted-foreground">
          <DateField
            source="meeting_start_time"
            options={{
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
            }}
          />
        </div>
        <div className="flex-1" />
        <DeleteButton />
      </div>
      {record?.id != null && (
        <MeetingAssetBar
          meetingId={record.id}
          assets={assets}
          playerRef={playerRef}
        />
      )}

      <VideoContext.Provider
        value={{
          videoRef,
          timestamps,
          runMatching,
          timestampsLoading,
          timestampsProgress,
          leadIn,
          meetingId: record?.id as Identifier | undefined,
        }}
      >
        {/* Two columns, each an independent vertical stack:
            left = Video 40% / Summary 60%;
            right = Deal at its natural height, remaining space split
            Transcript 30% / Tasks 70% (the tasks panel is the workbench). */}
        <div className="grid grid-cols-2 gap-3 flex-1 min-h-0 overflow-hidden">
          {/* Left column */}
          <div className="grid grid-rows-[2fr_3fr] gap-3 min-h-0 overflow-hidden">
            {/* Video */}
            <div
              className={cn(
                "min-h-0 overflow-hidden",
                link.ringFor("screen", "camera", "audio"),
              )}
            >
              {hasPlayable ? (
                <RecordingPlayer
                  meetingRef={record?.id as number | undefined}
                  videoRef={videoRef}
                  initialKind={link.trackKind}
                  controlRef={playerRef}
                  onActiveChange={() => setVideoSwitchKey((k) => k + 1)}
                />
              ) : (
                <AssetPlaceholder
                  subject="recording"
                  state={mediaState(assets.row)}
                />
              )}
            </div>

            {/* Summary */}
            <div
              className={cn("min-h-0 overflow-hidden", link.ringFor("summary"))}
            >
              <SummaryPanel />
            </div>
          </div>

          {/* Right column */}
          <div className="grid grid-rows-[2fr_3fr] gap-3 min-h-0 overflow-hidden">
            {/* Transcript + Deal (top 40%) */}
            <div className="flex flex-col gap-3 min-h-0 overflow-hidden">
              {/* Transcript */}
              <div
                className={cn(
                  "flex-1 min-h-0 overflow-hidden",
                  link.ringFor("transcript"),
                )}
              >
                {transcriptCues.length > 0 ? (
                  <TranscriptPanel
                    videoRef={videoRef}
                    cues={transcriptCues}
                    videoKey={videoSwitchKey}
                    highlightTime={linkTime ?? undefined}
                  />
                ) : (
                  <AssetPlaceholder
                    subject="transcript"
                    state={assets.state("transcript")}
                  />
                )}
              </div>

              {/* Deal */}
              <div className="shrink-0">
                <DealSection />
              </div>
            </div>

            {/* Tasks + Actions */}
            <div className="min-h-0 overflow-hidden border rounded-lg flex flex-col">
              <div className="flex items-center gap-2 p-2 bg-muted/50 border-b shrink-0">
                <span className="text-sm font-medium text-muted-foreground flex-1">
                  <Translate i18nKey="resources.zoom_meeting_summaries.fields.tasks" />
                </span>
                <ReParseButton />
              </div>
              <ScrollArea className="flex-1 min-h-0">
                <div className="p-4 flex flex-col gap-3">
                  <RecordingCoverageNote span={span} />
                  <TaskMatchSummary />
                  <AssigneeResolutionPanel />
                  {/* Hide the whole iterator row (incl. its remove control)
                      for customer-bound drafts — TaskInputs renders only a
                      marker for them. */}
                  <div className="[&_li:has([data-external-hidden])]:hidden">
                    <ArrayInput source="tasks" label={false}>
                      {/* Row removal lives INSIDE TaskInputs (RemoveDraftButton)
                          so the control sits in the row's action cluster. */}
                      <SimpleFormIterator
                        inline
                        disableReordering
                        disableRemove
                      >
                        <TaskInputs />
                      </SimpleFormIterator>
                    </ArrayInput>
                  </div>
                </div>
              </ScrollArea>
              <div
                role="toolbar"
                className="flex px-4 pt-3 pb-3 border-t bg-background flex-row justify-end gap-2 shrink-0"
              >
                {/* Scoped to the task drafts, which is all these ever wrote. The
                    summary panel has its own Save; the page has none. */}
                <TaskListControls />
                <WithRecord<ZoomMeetingSummary>
                  render={(r) =>
                    r.status === "pending" ? <ProcessButton /> : null
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </VideoContext.Provider>
    </div>
  );
};

export const ZoomMeetingSummaryEdit = () => {
  return (
    <EditBase mutationMode="pessimistic" redirect={false}>
      {/* Every successful write reconciles the record cache, and ra-core resets
          the form on any record change. Without keepDirtyValues each save would
          wipe whatever the user has typed since. */}
      {/* onSubmit no-op: the <Form> renders a real <form>, and HTML implicit
          submission fires on Enter in any single-line input even with no submit
          button. Without this, Enter in the deal or assignee autocomplete would
          route every form value through saveContext.save, outside the queue.
          ProcessButton is unaffected — it calls form.handleSubmit directly. */}
      <Form resetOptions={{ keepDirtyValues: true }} onSubmit={() => undefined}>
        <MeetingSaveProvider>
          <LeaveGuard />
          <EditContent />
        </MeetingSaveProvider>
      </Form>
    </EditBase>
  );
};
